import { Box, Button, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'

import { Maybe } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ItemProps, ListItemImage } from '@/components/organisms'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  fetching: boolean
  hasNextPage: boolean
  customProfileUpdating: boolean
  items: ItemProps[]
  default: Maybe<ItemProps>
}

type CustomProfileEditCellProps = {
  thumbnail: Maybe<ItemProps['thumbnail']>
}

export type Action = {
  onSaveCustomProfile: (avatarItemId: Maybe<string>) => Promise<void>
  onFetchMore: () => Promise<void>
}

export const CustomProfileEditAvatarTemplate: Template<State, Action> = ({
  state,
  onSaveCustomProfile,
  onFetchMore,
}) => {
  const { t } = useTranslation('customProfileEditAvatar')
  const { showError } = useMessage()
  const { routerNames, routerPages } = useRouter()
  const [selected, setSelected] = useState<Maybe<ItemProps>>(null)

  useEffect(() => {
    if (state.default) {
      setSelected(state.default)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.default?.id])

  const handleSaveCustomProfile = () => {
    onSaveCustomProfile(selected?.id || null).catch(showError)
  }
  return (
    <BaseLayout
      header={{
        back: true,
        title: t('title'),
        defaultBackPage: routerPages.MyProfile,
        right: (
          <Button
            size="small"
            onClick={handleSaveCustomProfile}
            disabled={
              state.customProfileUpdating || state.default?.id === selected?.id
            }
            loading={state.customProfileUpdating}
            sx={{ mr: -1 }}
          >
            {t('save')}
          </Button>
        ),
      }}
    >
      <Head title={routerNames.CustomProfileEditAvatar} />
      {!state.loading && (
        <>
          <CustomProfileEditCell thumbnail={selected?.thumbnail ?? null} />
          <ListItemImage
            items={state.items}
            defaultValue={state.default?.id ?? ''}
            selected={selected}
            onSelected={setSelected}
            sx={{ minHeight: 276 }}
            avatarProps={{ sx: { minHeight: 96 } }}
            loading={state.fetching}
            hasNextPage={state.hasNextPage}
            onFetchMore={onFetchMore}
          />
        </>
      )}
    </BaseLayout>
  )
}

export const CustomProfileEditCell: Cell<CustomProfileEditCellProps> = ({
  thumbnail,
}) => {
  const { t } = useTranslation('customProfileEditAvatar')
  if (!thumbnail?.uri) {
    return (
      <CellFrame>
        <Stack
          sx={{ width: '100%', height: 400, bgcolor: 'grey.50' }}
          alignItems="center"
          justifyContent="center"
        >
          <Stack spacing={3}>
            <Typography variant="h5" align="center">
              {t('avatarNotSetTitle')}
            </Typography>
            <Typography variant="body1" align="center">
              {t('avatarNotSetDesc')}
            </Typography>
          </Stack>
        </Stack>
      </CellFrame>
    )
  }
  return (
    <CellFrame>
      <Box
        sx={{
          width: '100%',
          height: 400,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundImage: `url('${thumbnail?.uri ?? ''}')`,
        }}
      />
    </CellFrame>
  )
}
